/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useCallback } from 'react';
import { SERVICE_DESK } from '@jira/platform__application-editions/src/application-key.tsx';
import { useOgTenantInfo } from '@jira/eoc__tenant-info/src/controllers/index.tsx';
import {
	BUTTON,
	DROPDOWN_ITEM,
} from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/action-subject-type.tsx';
import {
	FIXED_ITEM_BUTTON,
	FIXED_ITEM_DROPDOWN_ACTION,
} from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/component-type.tsx';
import { MEATBALL_DROPDOWN } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/item-id.tsx';
import {
	LEVEL_ONE,
	LEVEL_TWO,
} from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/level.tsx';
import { SECTION_ITEM_OPERATIONS } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/section-type.tsx';
import { MENU_ID_ANALYTIC_EVENT_IDS } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/side-nav.tsx';
import { MENU_ID_OPERATIONS } from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import {
	type OnCloseProps,
	L1_MENU_ID,
} from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { HideL1MenuItemSection } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/ui/hide-l1-menu-item-action/index.tsx';
import { MoreNavMenuButton } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/ui/more-nav-menu-button/main.tsx';
import type { MoreNavMenuButtonMenuItemsProps } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/ui/more-nav-menu-button/submenu/index.tsx';
import { useStopPropagation } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/use-stop-propagation/index.tsx';
import IconButton from '@atlaskit/button/icon/button';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { token } from '@atlaskit/tokens';
import Section from '@atlaskit/menu/section';
import LinkItem from '@atlaskit/menu/link-item';
import Settings from '@atlaskit/icon/core/settings';
import { createRouterSelector } from '@atlassian/react-resource-router/src/controllers/router-store/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import {
	fireUIAnalytics,
	useAnalyticsEvents,
	type UIAnalyticsEvent,
} from '@atlassian/jira-product-analytics-bridge';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { fg } from '@atlassian/jira-feature-gating';
import { PopinIcon, PopoutIcon } from './icons/index.tsx';
import { useAlertsPopoutStore } from './useAlertsPopoutStore.tsx';

const generalConfiguration = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.operations-expandable-menu-item.action-buttons.general-configuration',

	defaultMessage: 'General configuration',
	description: 'Used as the alt text for an icon button that when used navigates to create project',
});

const popOutLabel = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.operations-expandable-menu-item.action-buttons.pop-out-label',

	defaultMessage: 'Pop out operations alert searches',
	description:
		'Tooltip message for the button to pop-out the operations alert searches menu from the sidebar into the main pages content.',
});

const popOutTooltip = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.operations-expandable-menu-item.action-buttons.pop-out-tooltip',

	defaultMessage: 'Pop out from sidebar',
	description:
		'Tooltip message for the button to pop-out the operations alert searches menu from the sidebar into the main pages content.',
});

const popInLabel = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.operations-expandable-menu-item.action-buttons.pop-in-label',

	defaultMessage: 'Pop in operations alert searches',
	description:
		'Accessibility label for the button to pop-in the operations alert searches menu from the main pages content into the sidebar.',
});

const popInTooltip = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.operations-expandable-menu-item.action-buttons.pop-in-tooltip',

	defaultMessage: 'Move back to sidebar',
	description:
		'Tooltip message for the button to pop-in the operations alert searches menu from the main pages content into the sidebar.',
});

const operations = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.operations-expandable-menu-item.action-buttons.operations',

	defaultMessage: 'Operations',
	description:
		'Name of the Operations section in the sidebar. Used for accessibility labels and tooltips.',
});

export function ActionButtons({ onClose }: OnCloseProps): React.JSX.Element {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const handleNavMenuOpen = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), {
			action: 'clicked',
			actionSubject: BUTTON,
			actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_OPERATIONS],
			attributes: {
				componentType: FIXED_ITEM_BUTTON,
				level: LEVEL_ONE,
				itemId: MEATBALL_DROPDOWN,
			},
		});
	}, [createAnalyticsEvent]);

	return (
		<MoreNavMenuButton
			onOpen={handleNavMenuOpen}
			MenuItems={MoreMenuItems}
			onClose={onClose}
			name={formatMessage(operations)}
		/>
	);
}

function MoreMenuItems({ onClose }: MoreNavMenuButtonMenuItemsProps) {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { formatMessage } = useIntl();
	const onCloseWithStopPropagation = useStopPropagation(
		(event: Event | React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element>) => {
			onClose?.(event);

			fireUIAnalytics(createAnalyticsEvent({}), {
				action: 'clicked',
				actionSubject: DROPDOWN_ITEM,
				actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_OPERATIONS],
				attributes: {
					componentType: FIXED_ITEM_DROPDOWN_ACTION,
					level: LEVEL_TWO,
					itemId: 'generalConfiguration',
					parentItemId: SECTION_ITEM_OPERATIONS,
				},
			});
		},
	);

	const tenantContext = useTenantContext();
	const { isOpsAdmin } = useOgTenantInfo();

	const shouldShowGeneralConfiguration =
		(tenantContext.isAdmin || isOpsAdmin) && tenantContext.licensedProducts[SERVICE_DESK];
	return (
		<>
			{shouldShowGeneralConfiguration ? (
				<Section>
					<LinkItem
						iconBefore={<Settings label="" spacing="spacious" color={token('color.icon')} />}
						href="/jira/settings/products/ops/jira-service-management-audit-logs"
						onClick={onCloseWithStopPropagation}
					>
						{formatMessage(generalConfiguration)}
					</LinkItem>
				</Section>
			) : null}
			{!fg('navx-global-side-nav') && (
				<HideL1MenuItemSection
					menuId={L1_MENU_ID.OPERATIONS}
					hasSeparator={shouldShowGeneralConfiguration}
				/>
			)}
		</>
	);
}

type PopoutToggleButtonProps = {
	onClick?: () => void;
};

const usePath = createRouterSelector(
	(state) => `${state.location.pathname}${state.location.search}`,
);

export const PopoutToggleButton = ({ onClick }: PopoutToggleButtonProps): React.JSX.Element => {
	const {
		state: { lastVisitedSearch },
		togglePoppedOut,
	} = useAlertsPopoutStore();
	const { formatMessage } = useIntl();
	const path = usePath();

	const handleToggle = (_: unknown, analyticsEvent: UIAnalyticsEvent) => {
		onClick?.();

		togglePoppedOut(path);

		fireUIAnalytics(analyticsEvent, 'jsmOperationsAlertsPopoutButtonToggle', {
			isPoppedOut: lastVisitedSearch,
		});
	};

	const iconButtonLabel = lastVisitedSearch
		? formatMessage(popInLabel)
		: formatMessage(popOutLabel);
	const tooltipContent = lastVisitedSearch
		? formatMessage(popInTooltip)
		: formatMessage(popOutTooltip);
	const Icon = lastVisitedSearch ? PopinIcon : PopoutIcon;

	return (
		<Tooltip content={tooltipContent}>
			{(tooltipProps) => (
				<IconButton
					{...tooltipProps}
					label={iconButtonLabel}
					onClick={handleToggle}
					icon={Icon}
					spacing="compact"
					appearance="subtle"
				/>
			)}
		</Tooltip>
	);
};
